import { useEffect, useRef, useState } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import type { ChatUIMessage } from '../lib/types'
import { ResultCard } from './ResultCard'
import { PlanCard } from './PlanCard'
import { PlanCtaCard } from './PlanCtaCard'
import { getStrings, type Lang } from '../i18n'

// react-markdown's default sanitizer rejects every scheme except http(s),
// mailto, tel, and relative — `sms:` is blocked, so the SMS coach CTA
// link in the post-result paragraph 2 wouldn't render as a tappable link
// without this opt-in. Allow `sms:` explicitly; everything else falls
// through to the safe default. Phone scheme is `sms:+1NXXNXXXXXX[?body=...]`.
const urlTransform = (url: string): string => {
  if (url.startsWith('sms:')) return url
  return defaultUrlTransform(url) ?? ''
}

// Under-18 guardrail escape hatch — mirrors MINOR_UNBLOCK_EMAIL on the server
// (lib/minor-gate.ts). Kept in sync manually; it's a brand constant.
const MINOR_UNBLOCK_EMAIL = 'support@nanorhino.com'

export function ChatStream({
  messages,
  streamingText,
  onSend,
  disabled,
  blocked = false,
  embed = false,
  conversationId,
  onRecalculate,
  onRetryPlan,
  onAcceptPlanOffer,
  glp1 = false,
  lang = 'en',
}: {
  messages: ChatUIMessage[]
  streamingText: string
  onSend: (text: string) => void
  disabled: boolean
  // Under-18 guardrail. When true the composer and every send affordance is
  // locked and a persistent block notice renders above the input — the sticky,
  // browser-local mirror of the server-side minor block. Independent of
  // `disabled` (which is the transient "assistant is thinking" lock).
  blocked?: boolean
  // Embed widget: once a result exists, show a single low-key "get coached"
  // link above the composer instead of the site's plan/SMS funnel.
  embed?: boolean
  conversationId: string | null
  onRecalculate: () => void
  onRetryPlan: () => void
  onAcceptPlanOffer: () => void
  // GLP-1 landing variant: swaps the cold-open hint + starter chips for
  // GLP-1 intents (so the opening message mentions the medication, reinforcing
  // the server-side glp1 capture). Default false on the standard /tdee page.
  glp1?: boolean
  lang?: Lang
}) {
  const t = getStrings(lang)
  // Any send path (composer submit, cold-open seed chips, plan-offer chip) is
  // dead while the assistant is streaming OR the minor block is active.
  const sendDisabled = disabled || blocked
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Stick-to-bottom guard. Naively scrolling to scrollHeight on every
  // update meant a freshly appended TDEE card (taller than the viewport,
  // especially on mobile) was instantly scrolled PAST — the user landed on
  // the plan question below it and never saw the breakdown. Rules:
  //   - a new calc card anchors the viewport to the card TOP (smooth) and
  //     releases the stick, so the typing question below can't yank the view
  //   - streaming only follows to the bottom while the user is already there
  //   - sending a message or tapping the plan chip re-engages the stick
  const stickToBottomRef = useRef(true)
  const prevMsgCountRef = useRef(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const isNewMessage = messages.length > prevMsgCountRef.current
    prevMsgCountRef.current = messages.length
    const last = messages[messages.length - 1]
    if (isNewMessage && last?.kind === 'calc') {
      const cards = el.querySelectorAll<HTMLElement>('.msg-card-calc')
      const target = cards[cards.length - 1]
      if (target) {
        stickToBottomRef.current = false
        const top =
          target.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 12
        el.scrollTo({ top, behavior: 'smooth' })
        return
      }
    }
    if (stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [messages, streamingText])

  function handleScroll() {
    const el = scrollRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  // Mobile keyboard pin. IM contract: when the keyboard opens/closes and the
  // user is at (or within ~120px of) the bottom of the thread, keep the
  // latest message glued to the input bar; when they've scrolled up into
  // history, never yank them back down.
  //
  // Two triggers, in the order iOS fires them on keyboard-open:
  //   1. input `focus` — fires BEFORE the viewport shrinks, so the
  //      near-bottom measurement here is the honest pre-keyboard one
  //   2. visualViewport `resize` — fires during/after the keyboard slide,
  //      possibly several times; by then the container has already shrunk
  //      (which alone inflates the measured bottom-distance past any
  //      threshold), so this path trusts stickToBottomRef instead — it
  //      survives the transition because container resizes don't fire
  //      scroll events on the container, only user scrolling updates it.
  //
  // The scroll itself runs on rAF — after App.tsx's --vvh write + reflow
  // (its visualViewport listener registers after ours: child effects mount
  // before parent effects), a bare synchronous scroll would target the
  // OLD layout height — and retries at 150/350ms to ride out the iOS
  // keyboard animation, during which the viewport keeps resizing.
  const settleTimersRef = useRef<number[]>([])

  const pinToBottom = () => {
    const scroll = () => {
      const el = scrollRef.current
      if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
    }
    stickToBottomRef.current = true
    requestAnimationFrame(scroll)
    settleTimersRef.current.forEach((t) => clearTimeout(t))
    settleTimersRef.current = [150, 350].map((ms) => window.setTimeout(scroll, ms))
  }

  function handleInputFocus() {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (nearBottom || stickToBottomRef.current) pinToBottom()
  }

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const onResize = () => {
      if (stickToBottomRef.current) pinToBottom()
    }
    vv.addEventListener('resize', onResize)
    return () => {
      vv.removeEventListener('resize', onResize)
      settleTimersRef.current.forEach((t) => clearTimeout(t))
    }
    // pinToBottom touches only refs — the first-render closure stays valid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Return focus to the input whenever the form becomes interactive again
  // (initial mount and every send → response cycle). Never steal focus onto a
  // locked composer when the minor block is active.
  useEffect(() => {
    if (!sendDisabled) inputRef.current?.focus()
  }, [sendDisabled])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = input.trim()
    if (!trimmed || sendDisabled) return
    stickToBottomRef.current = true
    onSend(trimmed)
    setInput('')
  }

  const isEmpty = messages.length === 0 && !streamingText
  return (
    <div className={isEmpty ? 'chat chat-empty' : 'chat'}>
      <div className="chat-scroll" ref={scrollRef} onScroll={handleScroll}>
        {messages.length === 0 && !blocked && (
          <div className="chat-hint">
            <div className="hint-header">
              <span className="kicker">{t.chat.hintKicker}</span>
              <span className="hint-meta">{t.chat.hintMeta}</span>
            </div>
            <ol className="hint-steps">
              {t.chat.steps.map((step) => (
                <li key={step.label}>
                  <span className="hint-step-label">{step.label}</span>
                  <span className="hint-step-detail">{step.detail}</span>
                </li>
              ))}
            </ol>
            <p className="hint-example">
              {t.chat.exampleLead}{' '}
              <em>{glp1 ? t.chat.exampleGlp1 : t.chat.exampleDefault}</em>
              <br />
              <span className="muted">{glp1 ? t.chat.mutedGlp1 : t.chat.mutedDefault}</span>
            </p>
            {/* Zero-typing first step. A single tap sends the opening message
               through the exact same path as typing + Send (onSend ===
               App.handleSend → POST /api/chat), so the conversation row is
               created and the interview starts without the cold composer
               gating cold paid-ad clicks. Only rendered at the empty
               cold-open state; once any message exists this whole hint block
               (and these chips) unmount. */}
            {glp1 ? (
              <div
                className="hint-start"
                role="group"
                aria-label={t.chat.ariaStartGlp1}
              >
                <button
                  type="button"
                  className="hint-start-primary"
                  onClick={() => {
                    stickToBottomRef.current = true
                    onSend(t.chat.seedGlp1Start)
                  }}
                  disabled={disabled}
                >
                  {t.chat.startPrimaryGlp1} <span aria-hidden="true">→</span>
                </button>
                <div className="hint-start-chips">
                  <button
                    type="button"
                    className="hint-start-chip"
                    onClick={() => {
                      stickToBottomRef.current = true
                      onSend(t.chat.seedGlp1Recomp)
                    }}
                    disabled={disabled}
                  >
                    {t.chat.startChipRecomp}
                  </button>
                  <button
                    type="button"
                    className="hint-start-chip"
                    onClick={() => {
                      stickToBottomRef.current = true
                      onSend(t.chat.seedGlp1ComingOff)
                    }}
                    disabled={disabled}
                  >
                    {t.chat.startChipComingOff}
                  </button>
                </div>
              </div>
            ) : (
              <div className="hint-start" role="group" aria-label={t.chat.ariaStartDefault}>
                <button
                  type="button"
                  className="hint-start-primary"
                  onClick={() => {
                    stickToBottomRef.current = true
                    onSend(t.chat.seedLose)
                  }}
                  disabled={disabled}
                >
                  {t.chat.startPrimaryDefault} <span aria-hidden="true">→</span>
                </button>
                <div className="hint-start-chips">
                  <button
                    type="button"
                    className="hint-start-chip"
                    onClick={() => {
                      stickToBottomRef.current = true
                      onSend(t.chat.seedMaintain)
                    }}
                    disabled={disabled}
                  >
                    {t.chat.startChipMaintain}
                  </button>
                  <button
                    type="button"
                    className="hint-start-chip"
                    onClick={() => {
                      stickToBottomRef.current = true
                      onSend(t.chat.seedBuild)
                    }}
                    disabled={disabled}
                  >
                    {t.chat.startChipBuild}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => {
          if (m.kind === 'text') {
            return (
              <div key={i} className={`msg msg-${m.role}`}>
                {m.role === 'assistant' ? (
                  <ReactMarkdown urlTransform={urlTransform}>{m.content}</ReactMarkdown>
                ) : (
                  m.content
                )}
              </div>
            )
          }
          if (m.kind === 'calc') {
            return (
              <div key={i} className="msg-card msg-card-calc">
                <ResultCard
                  result={m.payload}
                  onRecalculate={onRecalculate}
                  lang={lang}
                />
              </div>
            )
          }
          if (m.kind === 'plan') {
            return (
              <div key={i} className="msg-card msg-card-plan">
                <PlanCard
                  markdown={m.markdown}
                  status={m.status}
                  error={m.error}
                  onRetry={onRetryPlan}
                  lang={lang}
                />
              </div>
            )
          }
          if (m.kind === 'cta') {
            return (
              <div key={i} className="msg-card msg-card-cta">
                <PlanCtaCard
                  conversationId={conversationId}
                  handoffToken={m.handoffToken}
                  country={m.country}
                  tdeeRecommended={m.tdeeRecommended}
                  tdeeLow={m.tdeeLow}
                  tdeeHigh={m.tdeeHigh}
                  isMinor={m.isMinor}
                  hasExistingHandoff={m.hasExistingHandoff}
                  isGlp1={m.isGlp1}
                  intent={m.intent}
                  honestPlan={m.honestPlan}
                  lang={lang}
                />
              </div>
            )
          }
          if (m.kind === 'plan_offer') {
            // Auto-hide once the plan flow has been triggered (either by
            // clicking this chip OR by the user typing "yes" → Haiku
            // classifier → plan_request). We look forward in the array
            // for any plan/cta bubble; if one exists, the offer is dead.
            const consumed = messages
              .slice(i + 1)
              .some((later) => later.kind === 'plan' || later.kind === 'cta')
            if (consumed) return null
            return (
              <div key={i} className="msg-plan-offer">
                <button
                  type="button"
                  className="plan-offer-chip"
                  onClick={() => {
                    // Re-engage follow-scroll so the plan card streams in view
                    // even if the user tapped the chip from mid-scroll.
                    stickToBottomRef.current = true
                    onAcceptPlanOffer()
                  }}
                  disabled={sendDisabled}
                >
                  {t.chat.planOfferChip} <span aria-hidden="true">→</span>
                </button>
              </div>
            )
          }
          return null
        })}
        {streamingText && (
          <div className="msg msg-assistant streaming">
            <ReactMarkdown urlTransform={urlTransform}>{streamingText}</ReactMarkdown>
            <span className="cursor">▊</span>
          </div>
        )}
      </div>
      {blocked && (
        <div className="chat-block-notice" role="status">
          <strong>{t.blocked.title}</strong>
          <span>
            {t.blocked.body}{' '}
            <a href={`mailto:${MINOR_UNBLOCK_EMAIL}`}>{MINOR_UNBLOCK_EMAIL}</a>.
          </span>
        </div>
      )}
      {embed && messages.some((m) => m.kind === 'calc') && (
        <div className="embed-cta">
          Want a coach for these numbers?{' '}
          <a href="https://nanorhino.com" target="_blank" rel="noopener">
            Get coached over text at NanoRhino →
          </a>
        </div>
      )}
      <form className="chat-input" onSubmit={submit}>
        <label htmlFor="chat-input" className="sr-only">
          {t.chat.srAnswer}
        </label>
        <input
          ref={inputRef}
          id="chat-input"
          name="chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={
            blocked
              ? t.blocked.placeholder
              : disabled
                ? t.chat.placeholderThinking
                : messages.length === 0
                  ? t.chat.placeholderStart
                  : t.chat.placeholderAnswer
          }
          disabled={sendDisabled}
          autoComplete="off"
        />
        <button type="submit" disabled={sendDisabled || !input.trim()}>
          {t.chat.send}
        </button>
      </form>
    </div>
  )
}
